import { AutoFields, AutoForm, SubmitField, authReducer, useForm } from '@bitstopco/bitstop-theme';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';

import { Button, DialogActions, DialogContent, Divider } from '@mui/material';
import toast from 'react-hot-toast';

import SCHEMAS from '@/forms/schemas';
import { useAppSelector } from '@/store';
import { useAddCashLogNoteMutation } from '@/store/api/services/machines/cashlogs';

import type { CaseCustomNoteModel } from '@/types';

/**
 * Add Note to Cash Clear modal.
 *
 * Mirrors the case / service-ticket note modals: an `AutoForm` over the shared
 * note schema that commits via boro (`POST v2/CashBox/{id}/notes`). Author is
 * derived from the auth token server-side; the form only carries the text.
 */
const CashLogCustomNote = ({ hide, props }: ModalChildrenProps) => {
  const { schema } = useForm(SCHEMAS.CASH_LOG_CUSTOM_NOTE);
  const [addCashLogNote] = useAddCashLogNoteMutation();

  const {
    selectors: { userInfo },
  } = authReducer;
  const userData = useAppSelector(userInfo);

  const clearId = (props as { id?: number })?.id ?? 0;

  const handleSubmit = async (data: CaseCustomNoteModel) => {
    const author = `${userData?.first_name ?? ''} ${userData?.last_name ?? ''}`.trim();
    try {
      await addCashLogNote({ id: clearId, body: data.content, author }).unwrap();
      toast.success('Note added');
      hide();
    } catch {
      toast.error('Could not add note. Please try again.');
    }
  };

  return (
    <AutoForm<CaseCustomNoteModel> showInlineError schema={schema} onSubmit={handleSubmit}>
      <DialogContent sx={{ my: 2 }}>
        <AutoFields />
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={hide}>Back</Button>
        <SubmitField variant="contained">Add note</SubmitField>
      </DialogActions>
    </AutoForm>
  );
};

export default CashLogCustomNote;
